const API_BASE_URL = process.env.REACT_APP_API_URL ?? '';

export async function scoreOriginalResume(content, jobDescription, accessToken) {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/resume/score-original`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ content, jobDescription }),
      }
    );
    if (!response.ok) return null;
    return response.json();
  } catch {
    return null;
  }
}

// Streams the NDJSON response from /generate-resume, calling onEvent(parsedLine)
// for each line as it arrives ({type:'result', ...} per document, then a single
// {type:'summary', ...} line at the end). Resolves with the summary once the
// stream ends normally; throws if the connection closes without ever sending
// a summary line (err.code = 'STREAM_CUT_OFF'), or if aborted (err.name = 'AbortError').
export async function generateDocument(docType, payload, accessToken, { onEvent, signal } = {}) {
  const response = await fetch(
    `${API_BASE_URL}/api/resume/generate-resume`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify(payload),
      signal,
    }
  );
  if (response.status === 402) {
    const err = new Error('Insufficient credits');
    err.code = 'NO_CREDITS';
    throw err;
  }
  if (!response.ok) {
    // Pre-stream error (400/500) — headers were never flushed for NDJSON, so the
    // body is still a plain JSON error object.
    const errorData = await response.json();
    throw new Error(errorData.message || errorData.error || `Failed to optimize ${docType}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let summary = null;

  const handleLine = (line) => {
    const trimmed = line.trim();
    if (!trimmed) return;
    const parsed = JSON.parse(trimmed);
    if (parsed.type === 'summary') summary = parsed;
    if (onEvent) onEvent(parsed);
  };

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop(); // last element is either '' or an incomplete line
    for (const line of lines) handleLine(line);
  }
  buffer += decoder.decode();
  if (buffer.trim()) handleLine(buffer);

  if (!summary) {
    const err = new Error('Generation stream ended unexpectedly');
    err.code = 'STREAM_CUT_OFF';
    throw err;
  }
  return summary;
}

export async function fetchCredits(accessToken) {
  try {
    const response = await fetch(`${API_BASE_URL}/api/license/credits`, {
      headers: { 'Authorization': `Bearer ${accessToken}` },
    });
    if (!response.ok) return null;
    const data = await response.json();
    if (typeof data.credits !== 'number') return null;
    return { credits: data.credits, isAdmin: data.isAdmin === true };
  } catch {
    return null;
  }
}

export async function createCheckoutSession(sku, accessToken) {
  const response = await fetch(`${API_BASE_URL}/api/stripe/create-checkout-session`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ sku }),
  });
  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.error || 'Failed to create checkout session');
  }
  return response.json(); // { url }
}
